import { ShareGenerator } from './share.js';
import { announce, announceProgress, announceSuccess, announceError, KeyboardNavigator } from './accessibility.js';
import * as Anim from './animationSystem.js';
import { hintManager } from './hintManager.js';
import { TaxonomicStorage } from './storage.js';

export class TaxonomicUI {
    constructor(game) {
        this.game = game;
        this.mainBoard = document.getElementById('main-board');
        this.deepDiveBoard = document.getElementById('deep-dive-board');
        this.mainInstructions = document.getElementById('main-instructions');
        this.deepDiveInstructions = document.getElementById('deep-dive-instructions');
        this.victoryScreen = document.getElementById('victory-screen');
        this.errorScreen = document.getElementById('error-screen');
        this.shareModal = document.getElementById('share-modal');
        this.categoryNotification = document.getElementById('category-complete-notification');
        this.notificationCategoryName = document.getElementById('notification-category-name');
        this.hintsSection = document.getElementById('hints-section');
        this.hintsToggle = document.getElementById('hints-toggle');
        this.hintsNames = document.getElementById('hints-names');
        this.hintsFull = document.getElementById('hints-full');
        this.keyboardNavigator = null;
        this.isInitialRender = true; // Track if this is the first render
        this.isAnimating = false; // Prevent race conditions with rapid clicks
        this.touchCooldown = false; // Prevent accidental touches after re-render
        this.calendar = null; // Calendar instance for puzzle archive
        this.onReplayPuzzle = null; // Callback for replaying a puzzle

        this.setupShareHandlers();
        this.setupHintsSection();
        hintManager.init();
    }

    setupShareHandlers() {
        // Copy button on victory screen
        document.getElementById('copy-share-button').addEventListener('click', async () => {
            const includeSpoilers = document.getElementById('include-spoilers').checked;
            const text = ShareGenerator.generate(this.game, true, includeSpoilers);
            const success = await ShareGenerator.copyToClipboard(text);

            if (success) {
                const feedback = document.getElementById('share-copy-feedback');
                feedback.classList.remove('hidden');
                setTimeout(() => feedback.classList.add('hidden'), 2000);
            }
        });

        // Old modal handlers (kept for backwards compatibility but modal not shown)
        document.getElementById('copy-button').addEventListener('click', async () => {
            const includeLink = document.getElementById('include-link').checked;
            const text = ShareGenerator.generate(this.game, includeLink);
            const success = await ShareGenerator.copyToClipboard(text);

            if (success) {
                const feedback = document.getElementById('copy-feedback');
                feedback.classList.remove('hidden');
                setTimeout(() => feedback.classList.add('hidden'), 2000);
            }
        });

        document.getElementById('close-modal').addEventListener('click', () => {
            this.shareModal.classList.add('hidden');
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.shareModal.classList.contains('hidden')) {
                this.shareModal.classList.add('hidden');
            }
        });

        document.getElementById('include-link').addEventListener('change', () => {
            this.updateShareText();
        });
    }

    setupHintsSection() {
        if (!this.hintsToggle) return;

        // Cycle through states: collapsed → names → full → collapsed
        this.hintsToggle.addEventListener('click', () => {
            if (this.hintsSection.classList.contains('hints-collapsed')) {
                this.hintsSection.classList.remove('hints-collapsed');
                this.hintsSection.classList.add('hints-names');
                this.hintsToggle.textContent = 'Click for more hints!';
            } else if (this.hintsSection.classList.contains('hints-names')) {
                this.hintsSection.classList.remove('hints-names');
                this.hintsSection.classList.add('hints-full');
                this.hintsToggle.textContent = 'Click to close hints';
            } else {
                this.hintsSection.classList.remove('hints-full');
                this.hintsSection.classList.add('hints-collapsed');
                this.hintsToggle.textContent = 'Today\'s Hints (may contain spoilers!)';
            }
        });
    }

    renderHintsSection() {
        if (!this.hintsSection || !this.game.state.currentPuzzle) {
            return;
        }

        const categories = this.game.state.currentPuzzle.categories;
        const completedIds = new Set(this.game.state.completedCategories || []);

        // Build category names (inline, separated by bullets)
        const names = categories.map(c => {
            const isCompleted = completedIds.has(c.id);
            return isCompleted ? `<s>${c.answer}</s>` : c.answer;
        }).join(' • ');
        this.hintsNames.innerHTML = `<strong>Today's categories:</strong> ${names}`;

        // Build full hints list
        this.hintsFull.innerHTML = '';
        categories.forEach(category => {
            const div = document.createElement('div');
            const hint = category.themeHint || '';
            const isCompleted = completedIds.has(category.id);
            const strikeStart = isCompleted ? '<s>' : '';
            const strikeEnd = isCompleted ? '</s>' : '';
            if (hint) {
                div.innerHTML = `${strikeStart}<strong>${category.answer}</strong> – ${hint}${strikeEnd}`;
            } else {
                div.innerHTML = `${strikeStart}<strong>${category.answer}</strong>${strikeEnd}`;
            }
            this.hintsFull.appendChild(div);
        });

        // Show hints section in main mode, hide in deep dive
        if (this.game.state.mode === 'main') {
            this.hintsSection.classList.remove('hidden');
        } else {
            this.hintsSection.classList.add('hidden');
        }
    }

    updateShareText() {
        const includeLink = document.getElementById('include-link').checked;
        const text = ShareGenerator.generate(this.game, includeLink);
        document.getElementById('share-text').value = text;
    }

    showShareModal() {
        this.updateShareText();
        this.shareModal.classList.remove('hidden');
    }

    /**
     * Show category complete notification with background color
     */
    showCategoryCompleteNotification(categoryId, categoryAnswer) {
        const categoryIndex = this.getCategoryIndex(categoryId);

        // Set category name in notification
        this.notificationCategoryName.textContent = categoryAnswer;

        // Show notification with bouncy animation and letter pop
        Anim.showCategoryNotification(this.categoryNotification, this.notificationCategoryName);

        // Apply subtle background color
        document.body.className = ''; // Clear existing classes
        document.body.classList.add(`category-bg-${categoryIndex}`);
    }

    /**
     * Hide category complete notification
     */
    hideCategoryCompleteNotification() {
        Anim.hideCategoryNotification(this.categoryNotification);
    }

    /**
     * Reset background color to default
     */
    resetBackgroundColor() {
        document.body.className = '';
    }

    /**
     * Render the current board based on game mode
     * @param {boolean} withCascade - Force cascade animation (for transitions)
     */
    render(withCascade = false) {
        if (withCascade) {
            this.isInitialRender = true;
        }

        // Always render stack area
        this.renderStackArea();

        // Render hints section (shows in main mode, hidden in deep dive)
        this.renderHintsSection();

        if (this.game.state.mode === 'main') {
            this.renderMainBoard();
            this.hideDeepDive();
        } else {
            this.renderDeepDive();
            this.hideMainBoard();
        }

        // Set up keyboard navigation
        this.setupKeyboardNavigation();
    }

    /**
     * Render the stack area showing matched pairs
     */
    renderStackArea() {
        const stackArea = document.getElementById('stack-area');
        if (!stackArea) return;

        stackArea.innerHTML = '';

        const stacksData = this.game.getStacksData();

        stacksData.forEach(stack => {
            const stackEl = document.createElement('div');
            stackEl.className = 'pair-stack';
            stackEl.dataset.categoryId = stack.categoryId;

            if (stack.complete) {
                stackEl.classList.add('complete');
            }

            // Check if this is the active deep dive category
            if (this.game.state.mode === 'deepdive' &&
                this.game.state.deepDiveContext?.categoryId === stack.categoryId) {
                stackEl.classList.add('active-deepdive');
            }

            // Create cards for each pair
            stack.pairs.forEach((pair, pairIndex) => {
                const card = document.createElement('div');
                card.className = 'stack-card';
                card.dataset.category = stack.categoryIndex;

                // Only show text on top card (last one)
                const isTopCard = pairIndex === stack.pairs.length - 1;
                if (isTopCard) {
                    if (stack.complete) {
                        card.innerHTML = `<span class="stack-card-text">${stack.categoryName}</span>`;
                    } else {
                        card.innerHTML = `<span class="stack-card-text">${stack.topWord}</span>`;
                    }
                }

                stackEl.appendChild(card);
            });

            // Accessibility
            const pairCount = stack.pairs.length;
            const ariaLabel = stack.complete
                ? `${stack.categoryName}: Complete`
                : `Stack ${stack.categoryIndex + 1}: ${pairCount} pair${pairCount !== 1 ? 's' : ''} matched`;
            stackEl.setAttribute('aria-label', ariaLabel);

            stackArea.appendChild(stackEl);
        });
    }

    /**
     * Get visible words with their original indices (excludes stacked words)
     * @returns {Array<{word: Object, originalIndex: number}>}
     */
    getVisibleWordsWithIndices() {
        const allWords = this.game.getCurrentWords();
        const stackedIndices = this.game.getStackedIndices();

        return allWords
            .map((word, index) => ({ word, originalIndex: index }))
            .filter(({ originalIndex }) => !stackedIndices.includes(originalIndex));
    }

    /**
     * Get day-of-week instruction text
     * @returns {string} Instruction text for the puzzle
     */
    getDayInstructions() {
        const date = new Date(this.game.state.puzzleDate);
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[date.getDay()];
        const categoryCount = this.game.getGridSize();
        return `Happy ${dayName}! Find words in ${categoryCount} categories below.`;
    }

    /**
     * Render main 4x4 board
     */
    renderMainBoard() {
        // Preserve scroll position during re-render
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        this.mainBoard.innerHTML = '';
        this.mainBoard.classList.remove('hidden', 'zoom-out');
        this.mainInstructions.classList.remove('hidden');

        const allWords = this.game.getCurrentWords();
        const visibleIndices = new Set(
            this.getVisibleWordsWithIndices().map(w => w.originalIndex)
        );

        // Use TOTAL word count for stable column layout
        const columnCount = this.calculateColumnCount(allWords.length);
        const wordsPerColumn = Math.ceil(allWords.length / columnCount);

        // Update instructions
        this.mainInstructions.querySelector('p').textContent = this.getDayInstructions();

        // Create column-based board container
        const board = document.createElement('div');
        board.className = 'column-board';
        board.dataset.columnCount = columnCount;

        // Create columns and place words by their ORIGINAL index
        const columns = [];
        for (let col = 0; col < columnCount; col++) {
            const column = document.createElement('div');
            column.className = 'board-column';
            column.dataset.column = col;
            columns.push(column);
            board.appendChild(column);
        }

        // Place each visible word in its original column
        allWords.forEach((word, originalIndex) => {
            if (!visibleIndices.has(originalIndex)) return;

            const columnIndex = Math.floor(originalIndex / wordsPerColumn);
            const column = columns[Math.min(columnIndex, columnCount - 1)];
            column.appendChild(this.createWordButton(word, originalIndex));
        });

        // Set tabindex on first visible button for keyboard navigation
        const firstButton = board.querySelector('.word-button');
        if (firstButton) {
            firstButton.setAttribute('tabindex', '0');
        }

        this.mainBoard.appendChild(board);

        // Adjust font sizes based on actual overflow after DOM is ready
        requestAnimationFrame(() => {
            this.adjustButtonFontSizes(board);
        });

        // Only animate cascade on initial load, not on every re-render
        if (this.isInitialRender) {
            Anim.animateGridCascade([...board.querySelectorAll('.word-button')], 12);
            this.isInitialRender = false;
        }

        // Restore scroll position after DOM update
        requestAnimationFrame(() => {
            window.scrollTo(0, scrollTop);
        });
    }

    /**
     * Calculate number of columns based on word count
     * Uses square root to approximate a square-ish layout
     * Caps at 4 columns on mobile for vertical scrolling
     */
    calculateColumnCount(wordCount) {
        const columns = Math.ceil(Math.sqrt(wordCount));
        const isMobile = window.innerWidth <= 768;
        return isMobile ? Math.min(columns, 4) : columns;
    }

    /**
     * Adjust font sizes on buttons to prevent single-word overflow
     * Multi-word text is allowed to wrap; single long words get shrunk
     */
    adjustButtonFontSizes(grid) {
        const buttons = grid.querySelectorAll('.word-button');
        const sizeClasses = ['text-small', 'text-smaller', 'text-tiny', 'text-micro'];

        // Create a hidden measurement element
        const measureEl = document.createElement('span');
        measureEl.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-weight:600;';
        document.body.appendChild(measureEl);

        buttons.forEach(button => {
            const text = button.textContent;
            const words = text.split(/\s+/);
            const buttonWidth = button.clientWidth - 16; // Account for padding

            // Remove any existing size classes
            sizeClasses.forEach(cls => button.classList.remove(cls));

            // Find the longest word
            const computedStyle = window.getComputedStyle(button);
            measureEl.style.fontSize = computedStyle.fontSize;
            measureEl.style.fontFamily = computedStyle.fontFamily;

            let longestWordWidth = 0;
            words.forEach(word => {
                measureEl.textContent = word;
                longestWordWidth = Math.max(longestWordWidth, measureEl.offsetWidth);
            });

            // If longest word is wider than button, shrink font
            let sizeIndex = -1;
            while (longestWordWidth > buttonWidth && sizeIndex < sizeClasses.length - 1) {
                sizeIndex++;
                if (sizeIndex > 0) {
                    button.classList.remove(sizeClasses[sizeIndex - 1]);
                }
                button.classList.add(sizeClasses[sizeIndex]);

                // Re-measure with new font size
                measureEl.style.fontSize = window.getComputedStyle(button).fontSize;
                longestWordWidth = 0;
                words.forEach(word => {
                    measureEl.textContent = word;
                    longestWordWidth = Math.max(longestWordWidth, measureEl.offsetWidth);
                });
            }
        });

        // Clean up measurement element
        document.body.removeChild(measureEl);
    }

    /**
     * Render deep dive board
     */
    renderDeepDive() {
        // Clean up existing mini-game before clearing DOM
        const miniGame = this.game.state.deepDiveContext?.miniGame;
        if (miniGame && typeof miniGame.destroy === 'function') {
            miniGame.destroy();
        }

        this.deepDiveBoard.innerHTML = '';
        this.deepDiveBoard.classList.remove('hidden', 'zoom-out');

        // Show deep dive instructions with category name
        this.deepDiveInstructions.classList.remove('hidden');
        document.getElementById('deep-dive-category').textContent =
            this.game.state.deepDiveContext.categoryAnswer;

        // Get the mini-game instance and render it
        const currentMiniGame = this.game.state.deepDiveContext.miniGame;
        if (currentMiniGame && typeof currentMiniGame.render === 'function') {
            currentMiniGame.render(this.deepDiveBoard);
        } else {
            console.error('Mini-game not found or does not have render method');
        }
    }

    /**
     * Create a word button element
     */
    createWordButton(word, index) {
        const button = document.createElement('button');
        button.className = 'word-button';
        button.textContent = word.text;
        button.dataset.index = index;

        // Font sizing will be adjusted after render based on actual overflow

        // Add ARIA label for screen readers
        const isPaired = this.game.isWordPaired(index);
        const isSelected = this.game.state.selectedWords.includes(index);

        let ariaLabel = word.text;
        if (isPaired) {
            ariaLabel += ', already paired';
        } else if (isSelected) {
            ariaLabel += ', selected';
        }
        button.setAttribute('aria-label', ariaLabel);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        // Set initial tabindex (all -1; first visible button gets 0 in renderMainBoard)
        button.setAttribute('tabindex', '-1');

        // Add state classes
        if (isSelected) {
            button.classList.add('selected');
        }

        if (isPaired) {
            button.classList.add('paired');
            button.setAttribute('aria-disabled', 'true');

            // Add category color for paired words in main mode
            if (this.game.state.mode === 'main' && word.categoryId) {
                const categoryIndex = this.getCategoryIndex(word.categoryId);
                button.dataset.category = categoryIndex;
            }
        }

        // Add click handler
        button.addEventListener('click', () => this.handleWordClick(index).catch(console.error));

        // Add hint handlers for long-press/hover
        if (word.hint) {
            hintManager.attachHintHandlers(button, word.hint);
        }

        return button;
    }

    /**
     * Get category index for color assignment
     */
    getCategoryIndex(categoryId) {
        const categories = this.game.state.currentPuzzle.categories;
        return categories.findIndex(cat => cat.id === categoryId);
    }

    /**
     * Set up keyboard navigation
     */
    setupKeyboardNavigation() {
        // Clean up existing navigator
        if (this.keyboardNavigator) {
            this.keyboardNavigator.destroy();
        }

        // Get current board container
        const container = this.game.state.mode === 'main' ? this.mainBoard : this.deepDiveBoard;
        const buttons = Array.from(container.querySelectorAll('.word-button:not(.paired)'));

        if (buttons.length === 0) return;

        // Set up new navigator
        this.keyboardNavigator = new KeyboardNavigator(container, buttons, {
            onSelect: (index, element) => {
                const wordIndex = parseInt(element.dataset.index);
                this.handleWordClick(wordIndex).catch(console.error);
            },
            wrap: true
        });
    }

    /**
     * Handle word click
     */
    async handleWordClick(index) {
        // Prevent race conditions with rapid clicks during animations
        // and accidental touches on buttons that moved into position
        if (this.isAnimating || this.touchCooldown) return;

        const result = this.game.selectWord(index);
        const words = this.game.getCurrentWords();
        const word = words[index];

        switch (result.action) {
            case 'select':
                Anim.animateSelect(document.querySelector(`[data-index="${index}"]`));
                announce(`${word.text} selected`);
                this.render();
                break;

            case 'deselect':
                Anim.animateDeselect(document.querySelector(`[data-index="${index}"]`));
                announce(`${word.text} deselected`);
                this.render();
                break;

            case 'already_paired':
                // Do nothing - word is already paired in deep dive
                announce(`${word.text} is already paired`);
                break;

            case 'pair_matched':
                this.isAnimating = true;
                try {
                    // Visually select the second button before animating the match
                    // (game state already cleared selectedWords, but we need the visual feedback)
                    const secondBtn = document.querySelector(`[data-index="${result.indices[1]}"]`);
                    if (secondBtn) {
                        secondBtn.classList.add('selected');
                        Anim.animateSelect(secondBtn);
                    }

                    announceSuccess('Pair matched!');
                    this.animatePairMatch(result.indices);

                    // Add matched pair to stack
                    const matchedWord = words[result.indices[1]];
                    const categoryId = matchedWord.categoryId;
                    this.game.addToStack(categoryId, result.indices, matchedWord.text);

                    // Announce stack update for screen readers
                    const word1Text = words[result.indices[0]].text;
                    const word2Text = words[result.indices[1]].text;
                    announce(`${word1Text} and ${word2Text} moved to matched pairs`);

                    // Render stack area to create target element
                    this.renderStackArea();

                    // Force layout reflow to ensure stack area is properly positioned
                    // before we measure it for animation
                    await new Promise(resolve => requestAnimationFrame(() =>
                        requestAnimationFrame(resolve)
                    ));

                    const targetStack = document.querySelector(`[data-category-id="${categoryId}"]`);

                    // Get matched buttons before they might be removed
                    const btn1 = this.mainBoard.querySelector(`[data-index="${result.indices[0]}"]`);
                    const btn2 = this.mainBoard.querySelector(`[data-index="${result.indices[1]}"]`);

                    // Animate cards to stack sequentially
                    if (targetStack) {
                        if (btn1) await Anim.animateToStack(btn1, targetStack, { duration: 300 });
                        if (btn2) await Anim.animateToStack(btn2, targetStack, { duration: 300 });
                    }

                    // Save state after stacking
                    TaxonomicStorage.saveState(this.game);

                    // Capture grid positions BEFORE re-render for FLIP animation
                    const gridContainer = this.mainBoard.querySelector('.main-grid');
                    const oldPositions = gridContainer ?
                        Anim.captureGridPositions(gridContainer, result.indices) : null;

                    // Set cooldown BEFORE re-render to block touches on new buttons
                    this.touchCooldown = true;
                    hintManager.disableHints();

                    // Re-render to remove stacked words from grid
                    this.render();

                    // Disable pointer events on new grid during collapse animation
                    const newGridContainer = this.mainBoard.querySelector('.main-grid');
                    if (newGridContainer) {
                        newGridContainer.classList.add('animating');
                    }

                    // Play collapse animation - remaining cells slide into place
                    if (oldPositions && newGridContainer) {
                        Anim.playGridCollapse(newGridContainer, oldPositions, { duration: 300 });
                    }

                    // Re-enable interactions after animation completes
                    setTimeout(() => {
                        this.touchCooldown = false;
                        hintManager.enableHints();
                        if (newGridContainer) {
                            newGridContainer.classList.remove('animating');
                        }
                    }, 350);
                } catch (e) {
                    console.error('Animation error in pair_matched:', e);
                } finally {
                    this.isAnimating = false;
                }
                break;

            case 'category_complete':
                this.isAnimating = true;
                try {
                    // Visually select the second button before animating the match
                    const secondBtnComplete = document.querySelector(`[data-index="${result.indices[1]}"]`);
                    if (secondBtnComplete) {
                        secondBtnComplete.classList.add('selected');
                        Anim.animateSelect(secondBtnComplete);
                    }

                    announceSuccess('Category complete!');
                    this.animatePairMatch(result.indices);

                    // Add final pair to stack and mark category complete
                    const completeWord = words[result.indices[1]];
                    const completeCategoryId = result.categoryId;
                    this.game.addToStack(completeCategoryId, result.indices, completeWord.text);
                    this.game.markStackComplete(completeCategoryId);

                    // Render stack area to create target element
                    this.renderStackArea();

                    // Force layout reflow to ensure stack area is properly positioned
                    await new Promise(resolve => requestAnimationFrame(() =>
                        requestAnimationFrame(resolve)
                    ));

                    const completeTargetStack = document.querySelector(`[data-category-id="${completeCategoryId}"]`);

                    // Get matched buttons and animate to stack
                    const completeBtn1 = this.mainBoard.querySelector(`[data-index="${result.indices[0]}"]`);
                    const completeBtn2 = this.mainBoard.querySelector(`[data-index="${result.indices[1]}"]`);

                    if (completeTargetStack) {
                        if (completeBtn1) await Anim.animateToStack(completeBtn1, completeTargetStack, { duration: 300 });
                        if (completeBtn2) await Anim.animateToStack(completeBtn2, completeTargetStack, { duration: 300 });
                    }

                    // Save state after stacking
                    TaxonomicStorage.saveState(this.game);

                    // Use setTimeout for remaining flow to avoid blocking
                    setTimeout(() => {
                        try {
                            // Mark category as complete and enter deep dive
                            this.game.state.completedCategories.push(result.categoryId);

                            // Create callback for when mini-game completes
                            const onMiniGameComplete = () => {
                                try {
                                    const exitResult = this.game.exitDeepDive();
                                    if (exitResult.action === 'game_complete') {
                                        announce('Puzzle complete!');
                                        this.showVictory();
                                    } else {
                                        announce(`Returning to main board`);
                                        this.render(true); // Cascade animation when returning
                                    }
                                } catch (e) {
                                    console.error('Animation error in onMiniGameComplete:', e);
                                } finally {
                                    this.isAnimating = false;
                                }
                            };

                            const deepDiveResult = this.game.enterDeepDive(result.categoryId, onMiniGameComplete);

                            // Show category complete notification with background color
                            this.showCategoryCompleteNotification(result.categoryId, deepDiveResult.categoryAnswer);

                            // Wait 2 seconds, then transition to deep dive
                            setTimeout(() => {
                                try {
                                    this.hideCategoryCompleteNotification();
                                    announce(`Entering deep dive for ${deepDiveResult.categoryAnswer}`);
                                    this.animateEnterDeepDive(deepDiveResult.categoryAnswer);
                                    setTimeout(() => {
                                        try {
                                            this.render();
                                        } catch (e) {
                                            console.error('Animation error in deep dive render:', e);
                                        } finally {
                                            this.isAnimating = false;
                                        }
                                    }, 1000);
                                } catch (e) {
                                    console.error('Animation error in deep dive transition:', e);
                                    this.isAnimating = false;
                                }
                            }, 2000);
                        } catch (e) {
                            console.error('Animation error in category complete flow:', e);
                            this.isAnimating = false;
                        }
                    }, 100);
                } catch (e) {
                    console.error('Animation error in category_complete:', e);
                    this.isAnimating = false;
                }
                break;

            case 'wrong_pair':
                announceError('Incorrect pair');
                this.animateWrongMatch(result.indices);
                setTimeout(() => this.render(), 500);
                break;

            case 'enter_deepdive':
                announce(`Entering deep dive for ${result.categoryAnswer}`);
                this.animateEnterDeepDive(result.categoryAnswer);
                setTimeout(() => this.render(), 1000);
                break;

            case 'deepdive_complete':
                announceSuccess('Deep dive complete!');
                this.animateDeepDiveComplete();
                setTimeout(() => {
                    const exitResult = this.game.exitDeepDive();
                    if (exitResult.action === 'game_complete') {
                        announceSuccess('Puzzle complete!');
                        this.showVictory();
                    } else {
                        announce('Returning to main board');
                        this.render(true); // Cascade animation when returning
                    }
                }, 1500);
                break;

            case 'wrong_category':
                announceError('Words must be from the same category');
                this.animateWrongMatch(result.indices);
                setTimeout(() => this.render(), 500);
                break;
        }
    }

    /**
     * Animate pair match
     */
    animatePairMatch(indices, categoryId = null) {
        const button1 = document.querySelector(`[data-index="${indices[0]}"]`);
        const button2 = document.querySelector(`[data-index="${indices[1]}"]`);

        // Get category color for particles
        let color = '#4CAF50'; // default green
        if (categoryId) {
            const categoryIndex = this.getCategoryIndex(categoryId);
            const colors = ['#2196F3', '#FFC107', '#4CAF50', '#9C27B0', '#FF5722',
                          '#00BCD4', '#E91E63', '#8BC34A', '#FF9800', '#673AB7'];
            color = colors[categoryIndex % colors.length];
        }

        if (button1 && button2) {
            Anim.animateMatchSuccess(button1, button2, color);
        }
    }

    /**
     * Animate wrong match
     */
    animateWrongMatch(indices) {
        if (!indices || indices.length < 2) return;
        const button1 = document.querySelector(`[data-index="${indices[0]}"]`);
        const button2 = document.querySelector(`[data-index="${indices[1]}"]`);

        if (button1 && button2) {
            Anim.animateMatchFail(button1, button2);
        }
    }

    /**
     * Animate entering deep dive
     */
    animateEnterDeepDive(categoryAnswer) {
        Anim.emitSound(Anim.SoundEvents.DIVE_IN);
        const board = this.mainBoard;
        board.classList.add('zoom-out');
    }

    /**
     * Animate deep dive completion
     */
    animateDeepDiveComplete() {
        Anim.emitSound(Anim.SoundEvents.DIVE_OUT);
        const board = this.deepDiveBoard;
        board.classList.add('zoom-out');
    }

    /**
     * Hide main board
     */
    hideMainBoard() {
        this.mainBoard.classList.add('hidden');
        this.mainInstructions.classList.add('hidden');
    }

    /**
     * Hide deep dive
     */
    hideDeepDive() {
        // Clean up mini-game listeners before hiding
        const miniGame = this.game.state.deepDiveContext?.miniGame;
        if (miniGame && typeof miniGame.destroy === 'function') {
            miniGame.destroy();
        }

        this.deepDiveBoard.classList.add('hidden');
        this.deepDiveInstructions.classList.add('hidden');
    }

    /**
     * Show victory screen
     */
    showVictory() {
        this.hideMainBoard();
        this.hideDeepDive();
        this.resetBackgroundColor(); // Reset to default background

        // Render victory content first (creates DOM elements we need to animate)
        this.renderVictory(this.game.state.currentPuzzle, this.game.state.puzzleDate);

        // Get category colors for confetti
        const categoryColors = ['#2196F3', '#FFC107', '#4CAF50', '#9C27B0', '#FF5722',
                               '#00BCD4', '#E91E63', '#8BC34A', '#FF9800', '#673AB7'];
        const usedColors = categoryColors.slice(0, this.game.state.currentPuzzle.categories.length);

        // Get category elements for staggered animation
        const categoryElements = [...document.querySelectorAll('.victory-category')];

        // Show victory with full celebration
        Anim.showVictory(this.victoryScreen, categoryElements, usedColors);
    }

    /**
     * Render victory screen with completed categories
     * @param {Object} puzzle - The puzzle data
     * @param {string} date - The puzzle date
     * @param {Object} options - Optional display options
     * @param {number} options.completionTime - Completion time in milliseconds
     */
    renderVictory(puzzle, date, options = {}) {
        const { completionTime = null } = options;

        const victoryMessage = document.getElementById('victory-message');
        victoryMessage.textContent = `You completed Taxonomic ${date}!`;

        // Add completion time display if provided
        const existingTimeDisplay = document.querySelector('.completion-time');
        if (existingTimeDisplay) {
            existingTimeDisplay.remove();
        }
        if (completionTime !== null) {
            const timeDisplay = document.createElement('p');
            timeDisplay.className = 'completion-time';
            timeDisplay.textContent = this.formatTime(completionTime);
            victoryMessage.parentNode.insertBefore(timeDisplay, victoryMessage.nextSibling);
        }

        // Create categories display with colored icons
        let categoriesHTML = '<div class="victory-categories">';
        puzzle.categories.forEach((category, idx) => {
            const colorEmoji = ShareGenerator.CATEGORY_COLORS[idx];
            categoriesHTML += `
                <div class="victory-category">
                    <span class="victory-category-icon">${colorEmoji}</span>
                    <span class="victory-category-name">${category.answer}</span>
                </div>
            `;
        });
        categoriesHTML += '</div>';

        // Insert categories display before share content
        const shareContent = document.getElementById('share-content');
        const existingCategories = document.querySelector('.victory-categories');
        if (existingCategories) {
            existingCategories.remove();
        }
        shareContent.insertAdjacentHTML('beforebegin', categoriesHTML);

        // Show share text (initially without spoilers)
        const shareDisplay = document.getElementById('share-display');
        const includeSpoilersCheckbox = document.getElementById('include-spoilers');

        const updateShareText = () => {
            const includeSpoilers = includeSpoilersCheckbox.checked;
            const shareText = ShareGenerator.generate(this.game, true, includeSpoilers);
            shareDisplay.textContent = shareText;
        };

        // Initial render without spoilers
        updateShareText();

        // Update when checkbox changes
        includeSpoilersCheckbox.addEventListener('change', updateShareText);

        // Create actions container for buttons
        const existingActions = document.querySelector('.victory-actions');
        if (existingActions) {
            existingActions.remove();
        }
        const actionsContainer = document.createElement('div');
        actionsContainer.className = 'victory-actions';

        // Always add replay button
        const replayBtn = document.createElement('button');
        replayBtn.className = 'replay-btn';
        replayBtn.textContent = 'Replay puzzle';
        replayBtn.addEventListener('click', () => {
            if (confirm('This will reset your progress. Replay puzzle?')) {
                this.onReplayPuzzle?.(date);
            }
        });
        actionsContainer.appendChild(replayBtn);

        // Always add calendar link
        const calendarLink = document.createElement('button');
        calendarLink.className = 'calendar-link';
        calendarLink.textContent = 'Play more puzzles \u2192';
        calendarLink.addEventListener('click', () => this.showCalendar());
        actionsContainer.appendChild(calendarLink);

        // Insert actions after share content
        shareContent.parentNode.insertBefore(actionsContainer, shareContent.nextSibling);
    }

    /**
     * Format time in milliseconds to a human-readable string
     * @param {number} ms - Time in milliseconds
     * @returns {string} Formatted time string
     */
    formatTime(ms) {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes === 0) {
            return `Completed in ${seconds} second${seconds !== 1 ? 's' : ''}`;
        } else if (seconds === 0) {
            return `Completed in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
        }
        return `Completed in ${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
    }

    /**
     * Show the calendar overlay modal
     */
    showCalendar() {
        const overlay = document.createElement('div');
        overlay.className = 'calendar-overlay';
        overlay.id = 'calendar-overlay';

        const modal = document.createElement('div');
        modal.className = 'calendar-modal';

        const closeBtn = document.createElement('button');
        closeBtn.className = 'calendar-close-btn';
        closeBtn.innerHTML = '\u00d7';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('click', () => this.hideCalendar());

        const title = document.createElement('h2');
        title.textContent = 'Puzzle Calendar';

        const container = document.createElement('div');
        container.id = 'calendar-container';

        modal.appendChild(closeBtn);
        modal.appendChild(title);
        modal.appendChild(container);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Close on overlay click (not modal)
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.hideCalendar();
        });

        // Refresh completion data before rendering to show latest state
        this.calendar?.loadCompletionData();
        this.calendar?.render(container);
    }

    /**
     * Hide the calendar overlay modal
     */
    hideCalendar() {
        const overlay = document.getElementById('calendar-overlay');
        if (overlay) overlay.remove();
    }

    /**
     * Show error screen
     */
    showError(message) {
        this.hideMainBoard();
        this.hideDeepDive();
        this.errorScreen.classList.remove('hidden');

        const errorMessage = document.getElementById('error-message');
        errorMessage.textContent = message;
    }
}
